import { useCallback, useRef, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@trillion/ui/components/ui/avatar";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@trillion/ui/components/ui/dialog";
import { Slider } from "@trillion/ui/components/ui/slider";
import { passportApi } from "../lib/passport-api";

interface AvatarUploaderProps {
  /** Current avatar URL (from GET /api/account). */
  currentUrl?: string | null;
  /** Initials shown when there's no image. */
  fallback: string;
  /** Called with the new CDN URL after a successful upload. */
  onUploaded?: (url: string) => void;
}

// Output is a 512×512 square (the crop is circular in the UI; a square covers the circle).
const OUTPUT_SIZE = 512;
const WEBP_QUALITY = 0.85;

// Magic-byte signatures for the image types we let a user pick (we don't trust the file extension).
// WebP is handled separately because its tag is at offset 8, not 0.
const IMAGE_SIGNATURES: number[][] = [
  [0xff, 0xd8, 0xff], // JPEG
  [0x89, 0x50, 0x4e, 0x47], // PNG
  [0x47, 0x49, 0x46, 0x38], // GIF8
];

async function sniffIsImage(file: File): Promise<boolean> {
  const buf = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const isWebp =
    buf.length >= 12 &&
    buf[0] === 0x52 && buf[1] === 0x49 && buf[2] === 0x46 && buf[3] === 0x46 && // RIFF
    buf[8] === 0x57 && buf[9] === 0x45 && buf[10] === 0x42 && buf[11] === 0x50; // WEBP
  return isWebp || IMAGE_SIGNATURES.some((sig) => sig.every((b, i) => buf[i] === b));
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load the selected image."));
    img.src = src;
  });
}

// Draw the chosen crop region into a 512² canvas and encode it as WebP — crop + resize + convert
// in one step. Returns null if the browser can't get a 2D context or encode WebP.
async function cropToWebp(src: string, area: Area): Promise<Blob | null> {
  const image = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/webp", WEBP_QUALITY));
}

export function AvatarUploader({ currentUrl, fallback, onUploaded }: AvatarUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayUrl, setDisplayUrl] = useState<string | null>(currentUrl ?? null);
  const [cropSrc, setCropSrc] = useState<string | null>(null); // non-null = crop dialog open
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onCropComplete = useCallback((_: Area, areaPx: Area) => setAreaPixels(areaPx), []);

  async function acceptFile(file: File | undefined | null) {
    if (!file) return;
    if (!(await sniffIsImage(file))) {
      toast.error("That file isn't a supported image (JPEG, PNG, GIF, or WebP).");
      return;
    }
    try {
      const src = await readAsDataUrl(file);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setAreaPixels(null);
      setCropSrc(src);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not read the selected file.");
    }
  }

  async function handleConfirm() {
    if (!cropSrc || !areaPixels) return;
    setUploading(true);
    try {
      const blob = await cropToWebp(cropSrc, areaPixels);
      // Guard: bail clearly if the browser couldn't encode WebP, so the server never gets a confusing
      // non-webp 400.
      if (!blob || blob.type !== "image/webp") {
        toast.error("Your browser couldn't prepare a WebP image. Try a different browser.");
        return;
      }
      const { avatar_url } = await passportApi.account.uploadAvatar(blob);
      setDisplayUrl(avatar_url);
      onUploaded?.(avatar_url);
      setCropSrc(null);
      toast.success("Avatar updated.");
    } catch (e) {
      // Client rejects AND API non-2xx (uploadAvatar throws with the server's message) land here.
      toast.error(e instanceof Error ? e.message : "Could not upload the avatar.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void acceptFile(e.dataTransfer.files?.[0]);
        }}
        className={`group relative rounded-full ring-offset-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          dragging ? "ring-2 ring-ring" : ""
        }`}
        aria-label="Change avatar"
      >
        <Avatar size="lg" className="size-20">
          {displayUrl ? <AvatarImage src={displayUrl} alt="Your avatar" /> : null}
          <AvatarFallback className="text-lg">{fallback}</AvatarFallback>
        </Avatar>
        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 transition group-hover:opacity-100">
          <Camera className="size-5" />
        </span>
      </button>
      <p className="text-xs text-muted-foreground">Drag an image here or click to change</p>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          void acceptFile(e.target.files?.[0]);
          e.target.value = ""; // allow re-selecting the same file
        }}
      />

      <Dialog open={cropSrc !== null} onOpenChange={(open) => !open && setCropSrc(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Position your photo</DialogTitle>
            <DialogDescription>Drag to reposition and use the slider to zoom.</DialogDescription>
          </DialogHeader>

          <div className="relative h-64 w-full overflow-hidden rounded-md bg-muted">
            {cropSrc ? (
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            ) : null}
          </div>

          <div className="flex items-center gap-3 px-1">
            <span className="text-xs text-muted-foreground">Zoom</span>
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.01}
              onValueChange={(v) => setZoom(v[0])}
              aria-label="Zoom"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCropSrc(null)} disabled={uploading}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirm} disabled={uploading || !areaPixels}>
              {uploading ? "Uploading…" : "Save photo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
